// src/app/api/repository.ts
import { fetchData } from '@app/utils/fetchData';

/** A single query parameter value, or several values for one repeatable parameter. */
export type QueryParamValue = string | number | boolean | null | undefined | Array<string | number>;

// Helper function to convert an object into a clean query string
export const buildQueryString = (params?: Record<string, QueryParamValue>): string => {
  if (!params) return '';

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    // A repeatable filter is sent as one parameter per value (?status=A&status=B), which is
    // what the search endpoints expect. An empty array means "no filter", not "match nothing".
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    // Only append valid values (ignores null/undefined so you don't get "?size=undefined")
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

export const createRepository = <T>(endpoint: string) => ({
  getAll: async (params?: Record<string, string | number | boolean | null | undefined>): Promise<T[]> => {
    return fetchData(`${endpoint}${buildQueryString(params)}`);
  },

  /**
   * How many rows the collection holds. Used by the data completeness check to find out whether
   * a store's one request can still hold everything - see store/dataCompletenessStore.ts.
   *
   * `params` has to carry whatever filter the store's own `getAll` carries, or the two are
   * counting different collections and the check reports a truncation that is not there. The
   * holidays are the reason it exists: their store asks for one `region`, the administration
   * screen's for every one, and the counts differ several-fold between them.
   *
   * Silent, like the other calls that answer a question about the service rather than fetch
   * something a user asked for: a single integer is never worth a loading backdrop.
   */
  count: async (params?: Record<string, QueryParamValue>): Promise<number> => {
    return fetchData(`${endpoint}/count${buildQueryString(params)}`, { silent: true });
  },

  getById: async (id: string | number): Promise<T> => {
    return fetchData(`${endpoint}/${id}`);
  },
  
  create: async (data: Partial<T>): Promise<T> => {
    return fetchData(endpoint, { method: 'POST', data });
  },
  
  update: async (id: string | number, data: Partial<T>): Promise<T> => {
    return fetchData(`${endpoint}/${id}`, { method: 'PUT', data });
  },
  
  remove: async (id: string | number): Promise<void> => {
    return fetchData(`${endpoint}/${id}`, { method: 'DELETE' });
  }
});

export const createNestedRepository = <T>(parentPath: string, childPath: string) => ({
  getAll: async (parentId: string | number, params?: Record<string, string | number | boolean | null | undefined>): Promise<T[]> => {
    return fetchData(`/${parentPath}/${parentId}/${childPath}${buildQueryString(params)}`);
  },
  
  getById: async (parentId: string | number, childId: string | number): Promise<T> => {
    return fetchData(`/${parentPath}/${parentId}/${childPath}/${childId}`);
  },
  
  create: async (parentId: string | number, data: Partial<T>): Promise<T> => {
    return fetchData(`/${parentPath}/${parentId}/${childPath}`, { method: 'POST', data });
  },
  
  update: async (parentId: string | number, childId: string | number, data: Partial<T>): Promise<T> => {
    return fetchData(`/${parentPath}/${parentId}/${childPath}/${childId}`, { method: 'PUT', data });
  },
  
  remove: async (parentId: string | number, childId: string | number): Promise<void> => {
    return fetchData(`/${parentPath}/${parentId}/${childPath}/${childId}`, { method: 'DELETE' });
  }
});